/**
 * @fileoverview Endless Runner Game Screen
 * @description Active gameplay screen for Endless Runner
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  AppState,
  AppStateStatus,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  RunnerDifficulty,
  RUNNER_DIFFICULTY_CONFIGS,
  RunnerWeekendConfig,
  DEFAULT_RUNNER_WEEKEND_CONFIG,
  RunnerUserStats,
  EMPTY_RUNNER_STATS,
  RunnerResult,
  CharacterId,
  CharacterAbilitySettings,
  DEFAULT_CHARACTER_ABILITY_SETTINGS,
  CHARACTER_CONFIGS,
} from "@/modules/endless-runner";
import { GameCanvas } from "@/modules/endless-runner/components/GameCanvas";
import { RunnerResultOverlay } from "@/modules/endless-runner/components/RunnerResultOverlay";
import { logActivity } from "@/lib/activity-log";
import { notifyTeamOfPoints, notifyAllUsersOfScore } from "@/modules/games/utils/teamNotifications";

type SessionMode = "free" | "weekend";

/**
 * EndlessRunnerGameScreen - Active gameplay
 */
export default function EndlessRunnerGameScreen() {
  const colors = useThemeColors();
  const { activeProfile, activeWeekend, userTeam } = useAuth();
  const { sessionId, difficulty: difficultyParam, character: characterParam } = useLocalSearchParams<{
    sessionId: string;
    difficulty?: string;
    character?: string;
  }>();

  const difficulty = (difficultyParam as RunnerDifficulty) || "medium";
  const characterId = (characterParam as CharacterId) || "opa";
  const config = RUNNER_DIFFICULTY_CONFIGS[difficulty];
  const characterConfig = CHARACTER_CONFIGS[characterId];

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [obstaclesAvoided, setObstaclesAvoided] = useState(0);
  const [jumps, setJumps] = useState(0);
  const [livesRemaining, setLivesRemaining] = useState(characterConfig.lives);
  const [specialJumpsUsed, setSpecialJumpsUsed] = useState(0);
  const [result, setResult] = useState<RunnerResult | null>(null);
  const [mode, setMode] = useState<SessionMode>("free");
  const [weekendConfig, setWeekendConfig] = useState<RunnerWeekendConfig>(
    DEFAULT_RUNNER_WEEKEND_CONFIG
  );
  const [highScore, setHighScore] = useState(0);
  const [familyHighScore, setFamilyHighScore] = useState(0);
  const [abilitySettings, setAbilitySettings] = useState<CharacterAbilitySettings>(
    DEFAULT_CHARACTER_ABILITY_SETTINGS
  );
  const [lottieConfig, setLottieConfig] = useState<{
    highScoreLottie: string;
    gameOverLottie: string;
  }>({
    highScoreLottie: "trophy",
    gameOverLottie: "Partying_Emoji",
  });

  // Timer
  const startTimeRef = useRef<number>(0);
  const timePlayedRef = useRef<number>(0);

  /**
   * Load session data
   */
  const loadSessionData = useCallback(async () => {
    if (!sessionId || !activeProfile?.id) return;

    try {
      // Get session
      const { data: session } = await supabase
        .from("game_sessions")
        .select("*")
        .eq("id", sessionId)
        .single();

      if (session) {
        setMode(session.mode as SessionMode);

        // Get weekend config if weekend mode
        if (session.mode === "weekend" && session.weekend_id) {
          const { data: configData } = await supabase
            .from("weekend_game_config")
            .select("points_config, custom_config")
            .eq("weekend_id", session.weekend_id)
            .eq("game_slug", "endless_runner")
            .single();

          if (configData?.custom_config) {
            setWeekendConfig({
              ...DEFAULT_RUNNER_WEEKEND_CONFIG,
              ...(configData.custom_config as Partial<RunnerWeekendConfig>),
            });
          }
        }
      }

      // Get user's high score
      const { data: statsData } = await supabase
        .from("user_game_stats")
        .select("stats")
        .eq("user_id", activeProfile.id)
        .eq("game_slug", "endless_runner")
        .eq("mode", session?.mode || "free")
        .single();

      if (statsData?.stats) {
        const stats = statsData.stats as RunnerUserStats;
        setHighScore(stats[difficulty]?.best_score || 0);
      }

      // Get family's best score (for weekend mode bonus)
      const { data: allStats } = await supabase
        .from("user_game_stats")
        .select("stats")
        .eq("game_slug", "endless_runner")
        .eq("mode", session?.mode || "free");

      if (allStats && allStats.length > 0) {
        let bestFamilyScore = 0;
        for (const entry of allStats) {
          const stats = entry.stats as RunnerUserStats;
          const diffScore = stats[difficulty]?.best_score || 0;
          if (diffScore > bestFamilyScore) {
            bestFamilyScore = diffScore;
          }
        }
        setFamilyHighScore(bestFamilyScore);
      }

      // Get Lottie config and ability settings from game definition
      const { data: gameDef } = await supabase
        .from("game_definitions")
        .select("default_config")
        .eq("slug", "endless_runner")
        .single();

      if (gameDef?.default_config) {
        const gameConfig = gameDef.default_config as Record<string, any>;
        setLottieConfig({
          highScoreLottie: gameConfig.high_score_lottie || "trophy",
          gameOverLottie: gameConfig.game_over_lottie || "Partying_Emoji",
        });
        if (gameConfig.ability_settings) {
          setAbilitySettings({
            ...DEFAULT_CHARACTER_ABILITY_SETTINGS,
            ...gameConfig.ability_settings,
          });
        }
      }

      // Start the game
      setIsPlaying(true);
      startTimeRef.current = Date.now();
    } catch (err) {
      console.error("[EndlessRunner] Error loading session:", err);
    }
  }, [sessionId, activeProfile?.id, difficulty]);

  useEffect(() => {
    loadSessionData();
  }, [loadSessionData]);

  /**
   * Handle app state changes (pause when backgrounded)
   */
  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState !== "active" && isPlaying && !isGameOver) {
        setIsPaused(true);
      }
    };

    const subscription = AppState.addEventListener("change", handleAppStateChange);
    return () => subscription.remove();
  }, [isPlaying, isGameOver]);

  /**
   * Calculate points earned
   */
  const calculatePoints = useCallback(
    (finalScore: number, isNewFamilyRecord: boolean): number => {
      if (mode !== "weekend") return 0;

      // Base participation points
      let points = weekendConfig.points_participation;

      // Bonus for setting a new family record (beating everyone's scores)
      if (isNewFamilyRecord) {
        points += weekendConfig.points_new_highscore;
      }

      return points;
    },
    [mode, weekendConfig]
  );

  /**
   * Handle score update from game canvas
   */
  const handleScoreUpdate = useCallback((newScore: number) => {
    setScore(newScore);
  }, []);

  /**
   * Handle obstacle avoided
   */
  const handleObstacleAvoided = useCallback(() => {
    setObstaclesAvoided((prev) => prev + 1);
  }, []);

  /**
   * Handle jump
   */
  const handleJump = useCallback(() => {
    setJumps((prev) => prev + 1);
  }, []);

  /**
   * Handle live lost (Oma)
   */
  const handleLiveLost = useCallback((remaining: number) => {
    setLivesRemaining(remaining);
  }, []);

  /**
   * Handle special jump used (Opa)
   */
  const handleSpecialJumpUsed = useCallback((used: number) => {
    setSpecialJumpsUsed(used);
  }, []);

  /**
   * Handle game over
   */
  const handleGameOver = useCallback(
    async (finalScore: number, finalObstacles: number, finalLives: number, finalSpecialJumps: number) => {
      if (!activeProfile?.id || !sessionId) return;

      setIsPlaying(false);
      setIsGameOver(true);
      setLivesRemaining(finalLives);
      setSpecialJumpsUsed(finalSpecialJumps);

      const timePlayedSeconds = Math.floor(
        (Date.now() - startTimeRef.current) / 1000
      );
      timePlayedRef.current = timePlayedSeconds;

      const isNewHighScore = finalScore > highScore;
      const isNewFamilyRecord = finalScore > familyHighScore;
      const pointsEarned = calculatePoints(finalScore, isNewFamilyRecord);

      try {
        // Update session
        await supabase
          .from("game_sessions")
          .update({
            status: "completed",
            completed_at: new Date().toISOString(),
            points_awarded: pointsEarned,
            game_state: {
              score: finalScore,
              obstaclesAvoided: finalObstacles,
              timePlayedSeconds,
              isGameOver: true,
              difficulty,
              characterId,
            },
          })
          .eq("id", sessionId);

        // Update user stats
        const { data: existingStats } = await supabase
          .from("user_game_stats")
          .select("*")
          .eq("user_id", activeProfile.id)
          .eq("game_slug", "endless_runner")
          .eq("mode", mode)
          .single();

        const currentStats =
          (existingStats?.stats as RunnerUserStats) || EMPTY_RUNNER_STATS;
        const diffStats = currentStats[difficulty] || {
          games_played: 0,
          best_score: 0,
          total_score: 0,
          total_obstacles_avoided: 0,
          best_time_seconds: 0,
          total_time_seconds: 0,
        };

        // Check if this is a new best score
        const isNewBestScore = finalScore > (diffStats.best_score || 0);

        const updatedDiffStats = {
          games_played: diffStats.games_played + 1,
          best_score: Math.max(diffStats.best_score, finalScore),
          // Update best_score_character only if this is a new best score
          best_score_character: isNewBestScore ? characterId : diffStats.best_score_character,
          total_score: diffStats.total_score + finalScore,
          total_obstacles_avoided:
            diffStats.total_obstacles_avoided + finalObstacles,
          best_time_seconds: Math.max(
            diffStats.best_time_seconds,
            timePlayedSeconds
          ),
          total_time_seconds: diffStats.total_time_seconds + timePlayedSeconds,
        };

        const updatedStats: RunnerUserStats = {
          ...currentStats,
          [difficulty]: updatedDiffStats,
          total_games: currentStats.total_games + 1,
          total_score: currentStats.total_score + finalScore,
          total_time_seconds:
            currentStats.total_time_seconds + timePlayedSeconds,
        };

        if (existingStats) {
          await supabase
            .from("user_game_stats")
            .update({
              stats: updatedStats,
              games_played: existingStats.games_played + 1,
              personal_best_score: Math.max(
                existingStats.personal_best_score || 0,
                finalScore
              ),
              total_points_earned:
                existingStats.total_points_earned + pointsEarned,
              last_played_at: new Date().toISOString(),
            })
            .eq("id", existingStats.id);
        } else {
          await supabase.from("user_game_stats").insert({
            user_id: activeProfile.id,
            game_slug: "endless_runner",
            mode,
            stats: updatedStats,
            games_played: 1,
            games_won: 1,
            personal_best_score: finalScore,
            total_points_earned: pointsEarned,
            last_played_at: new Date().toISOString(),
          });
        }

        // Weekend mode: add points to team and send notifications
        if (mode === "weekend" && pointsEarned > 0 && userTeam?.id) {
          // Update team score
          const { data: teamData } = await supabase
            .from("teams")
            .select("total_score")
            .eq("id", userTeam.id)
            .single();

          const previousTotal = teamData?.total_score || 0;
          const newTotal = previousTotal + pointsEarned;

          if (teamData) {
            await supabase
              .from("teams")
              .update({ total_score: newTotal })
              .eq("id", userTeam.id);
          }

          // Record in score_history for the scores screen
          await supabase.from("score_history").insert({
            team_id: userTeam.id,
            points: pointsEarned,
            previous_total: previousTotal,
            new_total: newTotal,
            reason: isNewFamilyRecord 
              ? `🏆 Nieuw familie record! Score: ${finalScore.toLocaleString()}`
              : `Endless Runner score: ${finalScore.toLocaleString()}`,
            game_type: "endless_runner",
            game_id: sessionId,
            awarded_to: activeProfile.id,
          });

          // Send notification to team members (with push)
          await notifyTeamOfPoints({
            earnerId: activeProfile.id,
            earnerName: activeProfile.full_name || "Iemand",
            teamId: userTeam.id,
            points: pointsEarned,
            gameName: "Endless Runner",
            reason: `Score: ${finalScore.toLocaleString()}`,
            weekendId: activeWeekend?.id,
            sessionId,
          });

          // Send silent in-app notification to ALL users
          if (activeWeekend?.id) {
            await notifyAllUsersOfScore({
              earnerId: activeProfile.id,
              earnerName: activeProfile.full_name || "Iemand",
              teamName: userTeam.name || "Team",
              points: pointsEarned,
              gameName: "Endless Runner",
              weekendId: activeWeekend.id,
              isNewRecord: isNewFamilyRecord,
            });
          }

          // Log activity
          await logActivity({
            user_id: activeProfile.id,
            action_type: "score.award",
            action_category: "score",
            target_type: "game_session",
            target_id: sessionId,
            target_name: "Endless Runner",
            metadata: {
              game_slug: "endless_runner",
              points: pointsEarned,
              score: finalScore,
              team_id: userTeam.id,
            },
            weekend_id: activeWeekend?.id,
          });
        }

        // Set result
        setResult({
          score: finalScore,
          distance: finalScore,
          obstaclesAvoided: finalObstacles,
          timeSeconds: timePlayedSeconds,
          difficulty,
          pointsEarned,
          isNewHighScore,
        });
      } catch (err) {
        console.error("[EndlessRunner] Error ending game:", err);
        // Still show result overlay even if saving fails
        setResult({
          score: finalScore,
          distance: finalScore,
          obstaclesAvoided: finalObstacles,
          timeSeconds: timePlayedSeconds,
          difficulty,
          pointsEarned: 0,
          isNewHighScore: false,
        });
      }
    },
    [
      activeProfile,
      sessionId,
      mode,
      difficulty,
      userTeam,
      activeWeekend,
      highScore,
      calculatePoints,
    ]
  );

  /**
   * Handle play again
   */
  const handlePlayAgain = useCallback(async () => {
    if (!activeProfile?.id) return;

    setResult(null);
    setIsGameOver(false);
    setScore(0);
    setObstaclesAvoided(0);
    setJumps(0);

    try {
      // Create new session
      const { data: session } = await supabase
        .from("game_sessions")
        .insert({
          game_slug: "endless_runner",
          created_by: activeProfile.id,
          participants: [
            {
              user_id: activeProfile.id,
              role: "player",
              name: activeProfile.full_name ?? "Speler",
              team_id: userTeam?.id,
            },
          ],
          mode,
          session_type: "solo",
          status: "active",
          game_state: { difficulty, isPlaying: true },
          weekend_id: mode === "weekend" ? activeWeekend?.id : null,
        })
        .select()
        .single();

      if (session) {
        // Update high score if needed
        if (result?.isNewHighScore) {
          setHighScore(result.score);
        }

        startTimeRef.current = Date.now();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error("[EndlessRunner] Error restarting:", err);
    }
  }, [activeProfile, userTeam, activeWeekend, mode, difficulty, result]);

  /**
   * Handle go back
   */
  const handleGoBack = useCallback(() => {
    router.back();
  }, []);

  /**
   * Toggle pause
   */
  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  /**
   * Format time
   */
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={handleGoBack}
          style={styles.backButton}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          <View style={[styles.difficultyBadge, { backgroundColor: config.color }]}>
            <Text style={styles.difficultyText}>{config.label}</Text>
          </View>
        </View>

        <TouchableOpacity
          onPress={togglePause}
          style={styles.pauseButton}
          disabled={isGameOver}
          accessibilityLabel={isPaused ? "Hervat" : "Pauzeer"}
          accessibilityRole="button"
        >
          <Ionicons
            name={isPaused ? "play" : "pause"}
            size={24}
            color={isGameOver ? colors.textTertiary : colors.text}
          />
        </TouchableOpacity>
      </View>

      {/* Stats Bar */}
      <View style={[styles.statsBar, { backgroundColor: colors.surface }]}>
        <View style={styles.statItem}>
          <Ionicons name="flash" size={16} color="#F59E0B" />
          <Text style={[styles.statValue, { color: colors.text }]}>
            {obstaclesAvoided}
          </Text>
        </View>
        <View style={styles.statItem}>
          <Ionicons name="trophy" size={16} color="#22C55E" />
          <Text style={[styles.statValue, { color: colors.text }]}>
            {highScore.toLocaleString()}
          </Text>
        </View>
        {mode === "weekend" && (
          <View style={styles.statItem}>
            <Ionicons name="star" size={16} color={colors.primary} />
            <Text style={[styles.statValue, { color: colors.primary }]}>
              Weekend
            </Text>
          </View>
        )}
      </View>

      {/* Game Canvas */}
      <View style={styles.gameContainer}>
        <GameCanvas
          difficulty={difficulty}
          characterId={characterId}
          abilitySettings={abilitySettings}
          isPlaying={isPlaying && !isGameOver}
          isPaused={isPaused}
          onScoreUpdate={handleScoreUpdate}
          onObstacleAvoided={handleObstacleAvoided}
          onGameOver={handleGameOver}
          onLiveLost={handleLiveLost}
          onSpecialJumpUsed={handleSpecialJumpUsed}
          onJump={handleJump}
        />
      </View>

      {/* Instructions */}
      {!isPlaying && !isGameOver && (
        <View style={styles.instructionsContainer}>
          <Text style={[styles.instructionsText, { color: colors.textSecondary }]}>
            Tik op het scherm om te springen
          </Text>
        </View>
      )}

      {/* Result Overlay */}
      {result && (
        <RunnerResultOverlay
          result={result}
          onPlayAgain={handlePlayAgain}
          onGoBack={handleGoBack}
          highScoreLottieKey={lottieConfig.highScoreLottie}
          gameOverLottieKey={lottieConfig.gameOverLottie}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 8,
  },
  headerCenter: {
    flex: 1,
    alignItems: "center",
  },
  difficultyBadge: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 20,
  },
  difficultyText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  pauseButton: {
    padding: 8,
  },
  statsBar: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 24,
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginHorizontal: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  statItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  statValue: {
    fontSize: 14,
    fontWeight: "600",
  },
  gameContainer: {
    flex: 1,
    paddingHorizontal: 16,
  },
  instructionsContainer: {
    padding: 16,
    alignItems: "center",
  },
  instructionsText: {
    fontSize: 14,
  },
});

